// earlyAccessSlice.js - "Request early access" submissions from the public
// landing page, listed on the portal Support tab (2026-10-10). Modelled on
// supportSlice.js (same fetched/status/error shape and the same
// mark-fetched-on-failure rule so the tab effect never loops).
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase.config";

export const fetchEarlyAccessRequests = createAsyncThunk(
  "earlyAccess/fetchEarlyAccessRequests",
  async (_, thunkAPI) => {
    try {
      const listFunction = httpsCallable(functions, "adminDashboardF-listEarlyAccessRequests");
      const result = await listFunction({});
      return result.data.requests || [];
    } catch (err) {
      console.error("Error fetching early access requests: ", err);
      return thunkAPI.rejectWithValue("Failed to load early access requests. Please try again.");
    }
  }
);

export const setEarlyAccessStatus = createAsyncThunk(
  "earlyAccess/setEarlyAccessStatus",
  async ({ requestId, status }, thunkAPI) => {
    try {
      const setStatusFunction = httpsCallable(functions, "adminDashboardF-setEarlyAccessRequestStatus");
      const result = await setStatusFunction({ requestId, status });
      if (result.data.success) {
        return { requestId, status };
      }
      return thunkAPI.rejectWithValue("Failed to update the request. Please try again.");
    } catch (err) {
      console.error("Error updating early access request: ", err);
      return thunkAPI.rejectWithValue("Failed to update the request. Please try again.");
    }
  }
);

const earlyAccessSlice = createSlice({
  name: "earlyAccess",
  initialState: {
    requests: [],
    status: "idle", // idle | loading | succeeded | failed
    error: null,
    fetched: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEarlyAccessRequests.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchEarlyAccessRequests.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.requests = action.payload || [];
        state.fetched = true;
        state.error = null;
      })
      .addCase(fetchEarlyAccessRequests.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.fetched = true;
      })
      .addCase(setEarlyAccessStatus.fulfilled, (state, action) => {
        const { requestId, status } = action.payload;
        const item = state.requests.find((r) => r.id === requestId);
        if (item) {
          item.status = status;
        }
        state.error = null;
      })
      .addCase(setEarlyAccessStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const selectEarlyAccessRequests = (state) => state.earlyAccess.requests;
export const selectEarlyAccessStatus = (state) => state.earlyAccess.status;
export const selectEarlyAccessError = (state) => state.earlyAccess.error;
export const selectEarlyAccessFetched = (state) => state.earlyAccess.fetched;

export default earlyAccessSlice.reducer;
